/* eslint-disable no-restricted-globals */
import { Link } from '@reach/router';
import { format } from 'date-fns';
import React, { useContext } from 'react';
import { AccountContext } from '../../context/account';
import { NotebookContext } from '../../context/NotebookContext';
import client from '../../lib/client';
import redirect from '../../lib/redirect';
import { deleteNoteMutation, notesQuery } from '../../queries/notes';
import {
  NoteDeleteMutation,
  NoteDeleteMutationVariables
} from '../../queries/__generated__/NoteDeleteMutation';
import { NotesQueryVariables } from '../../queries/__generated__/NotesQuery';
import { ButtonIconRound, ScrollView, Icon } from '../ui';
import Caption from './Caption';
import DataCard from './DataCard';
import PrivateNote from './PrivateNote';
import { variables } from '../utils';
import styled from 'styled-components';
import { NavigatorExtended } from '.';
import { back } from '../../lib/helpers/navigate';

interface NoteReadOnly {
  note: any;
}

const SideBar = styled.div.attrs({
  className: 'flex-none flex flex-column h-100'
})`
  width: 50vw;
  @media (min-width: ${variables.large}px) {
    width: 40vw;
  }
`;

const NoteReadOnlyNS: React.FC<NoteReadOnly> = ({ note }) => {
  const { notebook } = useContext(NotebookContext);
  const accountContext = useContext(AccountContext);

  const canEditNote = () => note.composedBy.id === accountContext.currentAccountMemberId;

  function deleteNote() {
    const confirmDelete = confirm('Are you sure you want to delete this note?');
    if (confirmDelete) {
      return client
        .mutate<NoteDeleteMutation, NoteDeleteMutationVariables>({
          mutation: deleteNoteMutation,
          variables: { input: { id: note.id } },
          refetchQueries: [
            {
              query: notesQuery,
              variables: { input: { notebookId: notebook.id } } as NotesQueryVariables
            }
          ]
        })
        .then(data => {
          const { errors } = data;
          if (errors) {
            throw new Error(errors[0].message);
          }
          redirect('/notebook');
        });
    }
    return false;
  }

  return (
    <>
      <div className="flex-auto flex">
        <ScrollView className="br b--light-gray ph4 pv5">
          <button onClick={() => back()} className="absolute pa3 top-0 left-0">
            <Icon name="close" />
          </button>
          <DataCard note={note} isEntryAuthor={false} />
        </ScrollView>

        <SideBar>
          <ScrollView className="ph3 ph4-l pv5">
            {note.context && <Caption caption={note.context} />}
            {note.content && <PrivateNote content={note.content} />}
          </ScrollView>
        </SideBar>
      </div>

      {/* Bottom Toolbar */}
      <div className="bg-white z-999 bt b--light-gray lh-copy flex-none flex items-center">
        <div className="flex-auto flex br b--light-gray">
          <div className="w-50 pv3 ph3 ph4-l">
            <div className="flex justify-between items-baseline">
              <span className="f7 f6-l">{format(note.notedAt, 'MMM. DD, YYYY')}</span>
              <span className="f8 ">{format(note.notedAt, 'h:mm a')}</span>
            </div>
            <p className="f8 mv0 light-silver">by {note.composedBy.displayName}</p>
          </div>

          {canEditNote() && (
            <div className="bl b--light-gray w-50 pv3 ph3 ph4-l flex items-center justify-center">
              <Link to="./edit" className="flex items-center mr3" data-testid="edit-button">
                <ButtonIconRound name="edit" />
                <span className="dn db-l f7 blue pl2">Edit</span>
              </Link>

              <div className="flex items-center pointer">
                <ButtonIconRound name="trash" onClick={deleteNote} data-testid="delete-button" />
                <button className="dn db-l f7 blue pl2" onClick={deleteNote}>
                  Delete
                </button>
              </div>
            </div>
          )}
        </div>

        <SideBar>
          <div className="h-100 flex items-center justify-end ph3 ph4-l">
            <button
              className="f7 blue pointer"
              onClick={() => {
                (navigator as NavigatorExtended)
                  .clipboard!.writeText(window.location.href)
                  .then(() => {
                    alert('Link copied to your clipboard!');
                  });
              }}
            >
              Copy Link
            </button>
          </div>
        </SideBar>
      </div>
    </>
  );
};

export default NoteReadOnlyNS;
